import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Mail, RefreshCw, LogOut, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VerifyEmailRequiredPage() {
    const { user, logout, refreshUser } = useAuth();
    const [resending, setResending] = useState(false);
    const [checking, setChecking] = useState(false);

    const handleResendVerification = async () => {
        if (!user?.email) return;
        
        setResending(true);
        try {
            await axios.post(`${API}/auth/resend-verification`, { email: user.email });
            toast.success('Verification email sent! Please check your inbox.');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to resend verification email');
        } finally {
            setResending(false);
        }
    };

    const handleCheckVerification = async () => {
        setChecking(true);
        try {
            await refreshUser();
            toast.info('Checking your verification status...');
        } catch (error) {
            toast.error('Failed to check verification status');
        } finally {
            setChecking(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md"
            >
                <div className="bg-card border border-border rounded-2xl p-8 text-center shadow-xl">
                    {/* Icon */}
                    <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Mail className="w-10 h-10 text-primary" />
                    </div>

                    {/* Greeting */}
                    <h1 className="text-2xl font-bold mb-2">Assalamu Alaikum!</h1>
                    <h2 className="text-xl font-semibold text-primary mb-4">
                        Please Verify Your Email
                    </h2>

                    {/* Message */}
                    <p className="text-muted-foreground mb-2">
                        We've sent a verification email to:
                    </p>
                    <p className="font-medium text-foreground mb-6 break-all">
                        {user?.email || 'your email address'}
                    </p>

                    <div className="bg-muted/50 rounded-lg p-4 mb-6 text-left">
                        <p className="text-sm text-muted-foreground">
                            Please check your inbox and click the verification link or enter the code on the{' '}
                            <Link to="/verify-email" className="text-primary hover:underline">
                                verification page
                            </Link>
                            .
                        </p>
                    </div>

                    {/* Actions */}
                    <div className="space-y-3">
                        <Link to="/verify-email" className="block">
                            <Button className="w-full" size="lg" data-testid="enter-code-btn">
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Enter Verification Code
                            </Button>
                        </Link>

                        <Button 
                            variant="outline" 
                            className="w-full" 
                            size="lg"
                            onClick={handleResendVerification}
                            disabled={resending}
                            data-testid="resend-verification-btn"
                        >
                            {resending ? (
                                <>
                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <Mail className="w-4 h-4 mr-2" />
                                    Resend Verification Email
                                </>
                            )}
                        </Button>

                        <Button 
                            variant="ghost" 
                            className="w-full" 
                            size="lg"
                            onClick={handleCheckVerification}
                            disabled={checking}
                            data-testid="check-status-btn"
                        >
                            {checking ? (
                                <>
                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                    Checking...
                                </>
                            ) : (
                                <>
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    I've Verified - Check Status
                                </>
                            )}
                        </Button>
                    </div>

                    {/* Divider */}
                    <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-border"></div>
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-card px-2 text-muted-foreground">or</span>
                        </div>
                    </div>

                    {/* Logout */}
                    <Button 
                        variant="ghost" 
                        className="w-full text-muted-foreground hover:text-destructive" 
                        onClick={logout}
                        data-testid="logout-btn"
                    >
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out & Use Different Email
                    </Button>

                    {/* Help text */}
                    <p className="text-xs text-muted-foreground mt-6">
                        Didn't receive the email? Check your spam folder or try resending.
                    </p>
                </div>
            </motion.div>
        </div>
    );
}
